import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { allElements, knownElements, newElements } from '../data/elements';

function ElementsTable() {
  const [filter, setFilter] = useState('all');
  const [selectedElement, setSelectedElement] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const getCategoryColor = (category) => {
    const colors = {
      'nonmetal': '#3AD0A1', 'noble-gas': '#7DD3FC', 'alkali-metal': '#FFB86A',
      'alkaline-earth': '#FFE082', 'metalloid': '#A3A3A3', 'halogen': '#FF7A3E',
      'post-transition': '#C4B5FD', 'transition': '#FCA5A5', 'lanthanide': '#F9A8D4',
      'actinide': '#F0ABFC', 'exotic': '#18D2FF', 'cosmic': '#A78BFA',
      'energy': '#FBBF24', 'stellar': '#FCD34D', 'photonic': '#60A5FA',
      'magnetic': '#EC4899', 'dark': '#4B5563', 'crystalline': '#93C5FD',
      'temporal': '#C084FC', 'acoustic': '#34D399', 'radioactive': '#FB923C'
    };
    return colors[category] || '#9CA3AF';
  };

  const getRarityColor = (rarity) => {
    const colors = {
      'Common': '#9CA3AF', 'Uncommon': '#10B981', 'Rare': '#3B82F6',
      'Ultra Rare': '#A855F7', 'Legendary': '#F59E0B', 'Mythic': '#EF4444'
    };
    return colors[rarity] || '#9CA3AF';
  };

  const filteredElements = allElements.filter(el => {
    const matchesFilter = filter === 'all' || 
                         (filter === 'known' && el.number <= 118) ||
                         (filter === 'new' && el.number > 118);
    
    const matchesSearch = el.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         el.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         el.number.toString().includes(searchTerm);
    
    const matchesCategory = categoryFilter === 'all' || el.category === categoryFilter;
    
    return matchesFilter && matchesSearch && matchesCategory;
  });

  const categories = [...new Set(allElements.map(el => el.category))].sort();

  return (
    <div className="min-h-screen pb-20">
      <Header breadcrumbs={[{ label: 'Elements Table' }]} />

      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-4xl font-bold mb-2">Universal Elements Database</h2>
          <p className="text-muted-foreground">
            Complete catalog of {allElements.length} elements: {knownElements.length} known + {newElements.length} newly discovered
          </p>
        </div>

        <div className="glass-card rounded-xl p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Element Type</label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full px-4 py-2 bg-white/5 border border-cyan-400/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 text-foreground"
                data-testid="type-filter"
              >
                <option value="all">All Elements ({allElements.length})</option>
                <option value="known">Known Elements ({knownElements.length})</option>
                <option value="new">New Discoveries ({newElements.length})</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Category</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-4 py-2 bg-white/5 border border-cyan-400/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 text-foreground"
                data-testid="category-filter"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Search</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Name, symbol, or number..."
                className="w-full px-4 py-2 bg-white/5 border border-cyan-400/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 text-foreground"
                data-testid="search-input"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3 mb-8" data-testid="elements-grid">
          {filteredElements.map((element) => (
            <button
              key={element.number}
              onClick={() => setSelectedElement(element)}
              className="glass-card rounded-lg p-3 text-center hover:scale-105 transition-all relative group"
              style={{ borderColor: getCategoryColor(element.category) + '40' }}
              data-testid={`element-${element.number}`}
            >
              <div className="text-xs text-muted-foreground mb-1">{element.number}</div>
              <div className="text-2xl font-bold mb-1" style={{ color: getCategoryColor(element.category) }}>
                {element.symbol}
              </div>
              <div className="text-xs text-foreground truncate">{element.name}</div>
              <div className="text-xs text-muted-foreground">{element.mass.toFixed(1)}</div>
              {element.number > 118 && (
                <div className="absolute top-1 right-1 w-2 h-2 bg-cyan-400 rounded-full"></div>
              )}
            </button>
          ))}
        </div>

        {filteredElements.length === 0 && (
          <div className="text-center py-20 text-muted-foreground">
            No elements found matching your criteria
          </div>
        )}

        {selectedElement && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setSelectedElement(null)}>
            <div className="glass-card rounded-xl p-8 max-w-2xl w-full" onClick={(e) => e.stopPropagation()} data-testid="element-modal">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <div className="text-sm text-muted-foreground mb-2">Element {selectedElement.number}</div>
                  <h3 className="text-4xl font-bold mb-2" style={{ color: getCategoryColor(selectedElement.category) }}>
                    {selectedElement.symbol} - {selectedElement.name}
                  </h3>
                </div>
                <button
                  onClick={() => setSelectedElement(null)}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="close-modal"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground">Atomic Mass</div>
                    <div className="text-xl font-semibold">{selectedElement.mass.toFixed(3)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Category</div>
                    <div className="text-xl font-semibold capitalize" style={{ color: getCategoryColor(selectedElement.category) }}>
                      {selectedElement.category.replace(/-/g, ' ')}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="text-sm text-muted-foreground">Discovered</div>
                  <div className="text-lg">{selectedElement.discovered}</div>
                </div>

                {selectedElement.number > 118 && (
                  <>
                    <div>
                      <div className="text-sm text-muted-foreground">Rarity</div>
                      <div className="text-lg font-semibold" style={{ color: getRarityColor(selectedElement.rarity) }}>
                        {selectedElement.rarity}
                      </div>
                    </div>

                    <div>
                      <div className="text-sm text-muted-foreground mb-2">Properties</div>
                      <div className="text-sm bg-white/5 rounded-lg p-3 border border-cyan-400/20">
                        {selectedElement.properties}
                      </div>
                    </div>
                  </>
                )}

                {selectedElement.number <= 118 && (
                  <div className="bg-white/5 rounded-lg p-3 border border-cyan-400/20">
                    <div className="text-sm text-cyan-400">Known Element</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      This is one of the {knownElements.length} elements currently known to science on Earth.
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={() => setSelectedElement(null)}
                className="mt-6 w-full py-3 bg-cyan-500/90 hover:bg-cyan-400 text-slate-900 font-semibold rounded-lg transition-all"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ElementsTable;
